import { useRef } from 'react';
import { useReactToPrint } from "react-to-print";
import { Row, Col, Typography, Space, Checkbox } from "antd";
import { getCurrentTime, dateTimeFormat9 } from "@/lib/dateTime";
import { getLangList } from '@/lib/i18n';
import ResponsiveTable from '@/components/responsiveTable';
import { ReButton } from "@/components/refineElement";
import { ClassSubjectItems, ClassType } from "@/containers/organization/classManagement/types";
import { StudentType } from "@/containers/humanResourceManagement/studentManagement/types";

interface Props {
  form: any;
  info: StudentType;
  subjectListForExam: ClassSubjectItems;
  classInfo: ClassType;
  year: string;
}

// GLevel
function ModalTranscriptType1(props: Props) {
  const { info, subjectListForExam, classInfo, year } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const subjectIdOP = subjectListForExam?.OP?.[0]?.subjectId ?? null;
  const scoreOP = subjectIdOP ? info[subjectIdOP] : '-'
  const dataSI = subjectListForExam?.SI?.map((subject) => ({ enName: subject.enName, score: info[subject.subjectId] })) ?? [];
  const dataSII = subjectListForExam?.SII?.map((subject) => ({ enName: subject.enName, score: info[subject.subjectId] })) ?? [];
  const dataSIII = subjectListForExam?.SIII?.map((subject) => ({ enName: subject.enName, score: info[subject.subjectId] })) ?? [];
  const dataOP = [
    { enName: 'A: Outstanding', cnName: '表現優異', score: 'A', checked: scoreOP === 'A' },
    { enName: 'B: Exceed Expectations', cnName: '表現良好', score: 'B', checked: scoreOP === 'B' },
    { enName: 'C: Satisfactory', cnName: '表現尚可', score: 'C', checked: scoreOP === 'C' },
    { enName: 'D: Unsatisfactory', cnName: '表現不佳', score: 'D', checked: scoreOP === 'D' },
    { enName: 'F: Needs Imporvement', cnName: '需待加強', score: 'F', checked: scoreOP === 'F' },
  ];
  const title = `${year} ${classInfo.semesterName} Final Report Card`;
  const reactToPrintFn = useReactToPrint({ contentRef, documentTitle: `${title} - ${info.nameCn}_${info.nameEn}` });
  const buttonT: any = getLangList("button");

  const columns1 = [
    { title: <Space><>Scholastic I</><>學科 I</></Space>, dataIndex: 'enName', width: '100px' },
    { title: <Space><>Performance</><>成績</></Space>, dataIndex: 'score' },
  ];

  const columns2 = [
    { title: <Space><>Scholastic II</><>學科 II</></Space>, dataIndex: 'enName', width: '100px' },
    { title: <Space><>Performance</><>成績</></Space>, dataIndex: 'score' },
  ];

  const columns3 = [
    { title: <Space><>Scholastic III</><>學科 III</></Space>, dataIndex: 'enName', width: '100px' },
    { title: <Space><>Performance</><>成績</></Space>, dataIndex: 'score' },
  ];

  const columns4 = [
    {
      title: <Space><>Overall Performance</><>整體表現</></Space>,
      dataIndex: 'score',
      render: (text: string, record: any) => (
        <Row>
          <Col span={6}><Checkbox checked={record.checked} /></Col>
          <Col span={10} style={{ textAlign: 'left' }}>{record.enName}</Col>
          <Col span={8} style={{ textAlign: 'left' }}>{record.cnName}</Col>
        </Row>
      ),
    },
  ];

  return (
    <Row justify="end" gutter={[0, 8]}>
      <Col>
        <ReButton iconName="download" onClick={() => reactToPrintFn()}>{buttonT.DOWNLOAD}</ReButton>
      </Col>

      <Col span={24}>
        <div className="modal-transcript-container transcript-container-type-1" ref={contentRef}>
          <Space direction="vertical" >
            <Row>
              <Col span={4}>
                <div className="transcript-icon" />
              </Col>

              <Col span={20}>
                <Typography.Title level={3}>
                  {title}
                </Typography.Title>
              </Col>
            </Row>

            <Row justify="end">
              <Col span={20}>
                <Row justify="space-between">
                  <Col>Student Name: {info.nameCn} {info.nameEn}</Col>
                  <Col>{getCurrentTime().format(dateTimeFormat9)}</Col>
                </Row>

                <Row>
                  <Col>Level: {classInfo.className}</Col>
                </Row>
              </Col>
            </Row>

            <Row gutter={[0, 12]}>
              <Col span={24}>
                <ResponsiveTable dataSource={dataSI} columns={columns1} displayScrollY={false} />
              </Col>

              <Col span={24}>
                <ResponsiveTable dataSource={dataSII} columns={columns2} displayScrollY={false} />
              </Col>

              <Col span={24}>
                <ResponsiveTable dataSource={dataSIII} columns={columns3} displayScrollY={false} />
              </Col>

              <Col span={24}>
                <ResponsiveTable dataSource={dataOP} columns={columns4} displayScrollY={false} />
              </Col>

              <Col span={24}>
                <Row>
                  <Col span={24} style={{ fontSize: '10.8px' }}>
                    A=90-100% B=80-89% C=70-79% D=60-69% F≦59%
                  </Col>

                  <Col span={24} style={{ fontSize: '10.8px' }}>
                    N/A=Not available; not enough data to calculate the final scores 無相關資料，無法計分。
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row justify="space-between" align="middle" style={{ marginTop: '30px' }}>
              <Col>
                <Typography.Text strong>
                  Teacher: {classInfo.teacherName}
                </Typography.Text>
              </Col>

              <Col>
                <Space>
                  <Typography.Text strong>Principal:</Typography.Text>
                  <div className='principal-sign'></div>
                </Space>
              </Col>
            </Row>
          </Space>
        </div>
      </Col>
    </Row>
  );
};

export default ModalTranscriptType1;